import { ERROR_MESSAGES, type ApiError as ApiErrorBody } from '@alicerce/contracts';

/** Erro da API no formato único `{ code, message, details? }` (guia, seção 1). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Erros de validação por campo (`details: [{ path, message }]` do ZodValidationPipe). */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details as { path?: string; message?: string }[]) {
        if (d.path !== undefined && d.message && !(d.path in out)) out[d.path] = d.message;
      }
    }
    return out;
  }
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof TypeError) return 'Sem conexão com o servidor. Confira sua internet e tente de novo.';
  return ERROR_MESSAGES.INTERNAL_ERROR;
}

export interface ApiClientOptions {
  baseUrl: string;
  onSessionExpired?: () => void;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  /** Não manda X-Tenant-Id (rotas de conta e onboarding). */
  noTenant?: boolean;
  /** Usa este negócio em vez do escolhido (o /me pede o de um negócio específico). */
  tenantId?: string;
  signal?: AbortSignal;
}

/**
 * Cliente HTTP do painel. O access token fica só em memória; o refresh token é um cookie httpOnly
 * (`/v1/auth`), renovado sozinho no primeiro 401. Renovações simultâneas compartilham a mesma promessa.
 */
export class ApiClient {
  private accessToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  private tenant: { id: string | null; impersonating: boolean } = { id: null, impersonating: false };
  private onSessionExpired: () => void;

  constructor(private readonly opts: ApiClientOptions) {
    this.onSessionExpired = opts.onSessionExpired ?? (() => {});
  }

  /** Chamado quando o refresh falha: a sessão acabou. */
  setSessionExpiredHandler(fn: () => void) {
    this.onSessionExpired = fn;
  }

  /** O negócio escolhido vai no header X-Tenant-Id; "entrar como" do suporte manda X-Staff-Impersonation. */
  setTenant(id: string | null, impersonating = false) {
    this.tenant = { id, impersonating };
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  hasSession() {
    return this.accessToken !== null;
  }

  /** Para o Socket.IO, que autentica no handshake. */
  token() {
    return this.accessToken;
  }

  url(path: string, query?: Query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    const s = qs.toString();
    return `${this.opts.baseUrl}${path}${s ? `?${s}` : ''}`;
  }

  get<T>(path: string, opts: RequestOptions = {}) {
    return this.request<T>('GET', path, opts);
  }
  post<T>(path: string, body?: unknown, opts: RequestOptions = {}) {
    return this.request<T>('POST', path, { ...opts, body });
  }
  put<T>(path: string, body?: unknown, opts: RequestOptions = {}) {
    return this.request<T>('PUT', path, { ...opts, body });
  }
  patch<T>(path: string, body?: unknown, opts: RequestOptions = {}) {
    return this.request<T>('PATCH', path, { ...opts, body });
  }
  delete<T>(path: string, opts: RequestOptions = {}) {
    return this.request<T>('DELETE', path, opts);
  }

  /** Renova o access token pelo cookie. Retorna false se a sessão acabou. */
  refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      try {
        const res = await fetch(this.url('/v1/auth/refresh'), { method: 'POST', credentials: 'include' });
        if (!res.ok) {
          this.accessToken = null;
          return false;
        }
        const body = (await res.json()) as { accessToken: string };
        this.accessToken = body.accessToken;
        return true;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    // O back-office (/internal) é da plataforma, não de um negócio.
    const tenantId = opts.noTenant || path.startsWith('/internal') ? null : (opts.tenantId ?? this.tenant.id);
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    if (tenantId && this.tenant.impersonating && tenantId === this.tenant.id) headers['X-Staff-Impersonation'] = '1';
    let body: BodyInit | undefined;
    if (opts.body instanceof FormData) body = opts.body;
    else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    const res = await fetch(this.url(path, opts.query), { method, headers, body, credentials: 'include', signal: opts.signal });
    if (res.status === 401 && !retried && !path.startsWith('/v1/auth/')) {
      if (await this.refresh()) return this.request<T>(method, path, opts, true);
      this.onSessionExpired();
    }
    if (!res.ok) throw await toApiError(res);
    if (res.status === 204) return undefined as T;
    const type = res.headers.get('content-type') ?? '';
    return (type.includes('application/json') ? await res.json() : await res.text()) as T;
  }
}

async function toApiError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as Partial<ApiErrorBody>;
    const code = body.code ?? 'INTERNAL_ERROR';
    const message = body.message ?? ERROR_MESSAGES[code as keyof typeof ERROR_MESSAGES] ?? ERROR_MESSAGES.INTERNAL_ERROR;
    return new ApiError(res.status, code, message, body.details);
  } catch {
    return new ApiError(res.status, 'INTERNAL_ERROR', ERROR_MESSAGES.INTERNAL_ERROR);
  }
}

/** Envia um arquivo direto ao S3 pela URL pré-assinada (PUT). */
export async function uploadToSignedUrl(url: string, file: File, contentType?: string) {
  // O Content-Type faz parte da assinatura: usa o que a API assinou, não o que o navegador adivinhou.
  const res = await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': contentType ?? file.type } });
  if (!res.ok) throw new ApiError(res.status, 'UPLOAD_FAILED', 'Não conseguimos enviar o arquivo. Tente de novo.');
}
